import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { AlertTriangle, Crown, Clock, ArrowRight, X } from 'lucide-react';

/**
 * Founder Countdown Banner
 * Displays urgency banners for founders based on their trial status.
 * 
 * Banner Types:
 * - 'reminder' (Day 45-55): Yellow/amber gentle reminder
 * - 'urgent' (Day 55-60): Orange/red urgent warning
 * - 'expired': Red expired notice
 */
export function FounderCountdownBanner({ 
  bannerType, 
  daysRemaining, 
  message, 
  onDismiss 
}) {
  const navigate = useNavigate();

  if (!bannerType) return null;

  const getBannerStyles = () => {
    switch (bannerType) {
      case 'expired':
        return {
          bg: 'bg-gradient-to-r from-red-500 to-rose-600',
          text: 'text-white',
          icon: AlertTriangle,
          buttonClass: 'bg-white text-red-600 hover:bg-red-50'
        };
      case 'urgent':
        return {
          bg: 'bg-gradient-to-r from-orange-500 to-red-500',
          text: 'text-white',
          icon: AlertTriangle,
          buttonClass: 'bg-white text-orange-600 hover:bg-orange-50'
        };
      case 'reminder':
      default:
        return {
          bg: 'bg-gradient-to-r from-amber-400 to-orange-400',
          text: 'text-white',
          icon: Clock,
          buttonClass: 'bg-white text-amber-600 hover:bg-amber-50'
        };
    }
  };

  const styles = getBannerStyles();
  const IconComponent = styles.icon;

  return (
    <div className={`${styles.bg} ${styles.text} py-3 px-4 relative`}>
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <Crown className="w-5 h-5 flex-shrink-0" />
          <IconComponent className="w-5 h-5 flex-shrink-0 animate-pulse" />
          <span className="font-medium text-sm md:text-base">
            {message || `${daysRemaining} days remaining to lock in your Founder pricing!`}
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            size="sm"
            className={styles.buttonClass}
            onClick={() => navigate('/pricing/founders')}
          >
            Continue at Founder Rate
            <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          
          {onDismiss && bannerType !== 'expired' && (
            <button 
              onClick={onDismiss}
              className="p-1 hover:bg-white/20 rounded"
              aria-label="Dismiss"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Compact version for sidebar or smaller spaces
 */
export function FounderCountdownCompact({ daysRemaining, bannerType }) {
  const navigate = useNavigate();
  
  if (!bannerType || bannerType === null) return null;
  
  const isUrgent = bannerType === 'urgent' || bannerType === 'expired';
  
  return (
    <div 
      className={`p-3 rounded-lg cursor-pointer transition-all ${
        isUrgent 
          ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white' 
          : 'bg-gradient-to-r from-amber-400 to-orange-400 text-white'
      }`}
      onClick={() => navigate('/pricing/founders')}
    >
      <div className="flex items-center gap-2 mb-1">
        <Crown className="w-4 h-4" />
        <span className="font-semibold text-sm">
          {bannerType === 'expired' ? 'Trial Ended' : `${daysRemaining} Days Left`}
        </span>
      </div>
      <p className="text-xs opacity-90">
        {bannerType === 'expired' 
          ? 'Lock in your Founder pricing now' 
          : 'Upgrade to keep premium access'
        }
      </p>
    </div>
  );
}

export default FounderCountdownBanner;
